/**
 * Laajennettu rivinäkymä tilauslistassa
 */

import { Wrench } from "lucide-react";
import { formatCurrency } from "@/lib/formatters";
import type { OrderListItem, WorkDetails } from "@/types/orders";
import { OrderWorkDetails } from "./OrderWorkDetails";
import { ConfigComponentsSummary } from "@/components/orders/detail/ConfigComponentsSummary";

interface OrderExpandedDetailsProps {
  order: OrderListItem;
}

export function OrderExpandedDetails({ order }: OrderExpandedDetailsProps) {
  // Etsi ensimmäinen rivi jolla on työn tietoja
  const lineWithWorkDetails = order.order_lines.find(
    (line) => line.work_details && Object.values(line.work_details).some((v) => v)
  );
  const workDetails = lineWithWorkDetails?.work_details as WorkDetails | undefined;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      {/* Order Lines */}
      <div>
        <h4 className="font-medium text-sm mb-2">Tilausrivit</h4>
        {order.order_lines.length > 0 ? (
          <ul className="space-y-1">
            {order.order_lines.map((line) => (
              <li key={line.id} className="text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {line.quantity}x {line.description}
                  </span>
                  <span className="font-medium">{formatCurrency(line.line_total)}</span>
                </div>
                {line.configurations?.config_components && line.configurations.config_components.length > 0 && (
                  <ConfigComponentsSummary components={line.configurations.config_components} />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Ei tilausrivejä</p>
        )}
      </div>

      {/* Work Details for pickup_work orders */}
      {order.order_type === "pickup_work" && (
        <div>
          <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
            <Wrench className="h-3.5 w-3.5" />
            Työn tiedot
          </h4>
          <OrderWorkDetails workDetails={workDetails} />
        </div>
      )}

      {/* Materials */}
      <div>
        <h4 className="font-medium text-sm mb-2">Materiaalit</h4>
        {order.order_materials.length > 0 ? (
          <ul className="space-y-1">
            {order.order_materials.map((mat) => (
              <li key={mat.id} className="text-sm text-muted-foreground">
                {mat.materials?.name || "Tuntematon"} - {mat.width}x{mat.length}x
                {mat.thickness}m ({mat.quantity} kpl)
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Ei materiaaleja</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <h4 className="font-medium text-sm mb-2">Muistiinpanot</h4>
        {order.notes ? (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {order.notes}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Ei muistiinpanoja</p>
        )}
      </div>
    </div>
  );
}
